/**
 * External dependencies
 */
import type { Attributes as WorkingAttributes } from '../../../../../packages/conversion-action-library/wc-add-to-cart/types';
import type { Product } from './woocommerce/events';

/**
 * Internal dependencies
 */
import type { ConvertibleAction, Convert } from '../../types';

type Attributes = Omit< WorkingAttributes, 'productId' > & {
	readonly productIds: ReadonlyArray< number >;
};

export function listenToWooCommerceAddToCartEvent(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	const actions = allActions.filter( isWooCommerceAddToCartAction );
	if ( ! actions.length ) {
		return;
	}

	const processProducts = ( products: ReadonlyArray< Product > ) => {
		if ( ! products.length ) {
			return;
		}

		convert(
			actions.filter(
				( action ) =>
					action.attributes.anyProduct ||
					intersect( products, action.attributes.productIds )
			)
		);
	};

	removeInitialListener( processProducts );
	window.addEventListener( 'nab-wc-product-added-to-cart', ( event ) =>
		processProducts(
			( event as CustomEvent< ReadonlyArray< Product > > ).detail
		)
	);
}

// =======
// HELPERS
// =======

function isWooCommerceAddToCartAction(
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > {
	return 'nab/wc-add-to-cart' === action.type;
}

function addInitialListener() {
	let previousProducts: ReadonlyArray< Product > = [];
	function listener( event: Event ) {
		const e = event as CustomEvent< ReadonlyArray< Product > >;
		previousProducts = [ ...previousProducts, ...e.detail ];
	}

	window.addEventListener( 'nab-wc-product-added-to-cart', listener );
	return ( processProducts: ( ps: ReadonlyArray< Product > ) => void ) => {
		processProducts( previousProducts );
		previousProducts = [];
		window.removeEventListener( 'nab-wc-product-added-to-cart', listener );
	};
}

const removeInitialListener = addInitialListener();

const intersect = (
	ps: ReadonlyArray< Product >,
	pids: ReadonlyArray< number >
) => ps.some( ( p ) => pids.includes( p.id ) );
